import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthService } from '@/services/auth';
import { queryKeys, invalidateQueries } from '@/lib/queryClient';
import { LoginCredentials, RegisterData, User, LoginResult } from '@/types';
import { createApiError } from '@/utils/errorHandler';

// Query hook for getting current user
export const useMe = () => {
  return useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: async (): Promise<User> => {
      try {
        return await AuthService.getMe();
      } catch (error) {
        throw createApiError(error, 'Get current user');
      }
    },
    enabled: !!AuthService.getToken(), // Only run if token exists
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
};

// Mutation hook for login
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: LoginCredentials): Promise<LoginResult> => {
      try {
        const authResponse = await AuthService.login(credentials);

        // Sincroniza IMEDIATAMENTE usuário no cache do React Query
        if (authResponse.user) {
          queryClient.setQueryData(queryKeys.auth.me, authResponse.user);
        }

        return { success: true, user: authResponse.user };
      } catch (error) {
        if (import.meta.env.DEV) console.error('[useLogin] Erro na mutação:', error);
        const apiError = createApiError(error, 'Login');
        return { success: false, error: apiError.message };
      }
    },
    onSuccess: (result) => {
      if (result.success && result.user) {
        // Invalidate any other auth-related queries
        invalidateQueries.auth();
      }
    },
    onError: (error) => {
      if (import.meta.env.DEV) console.error('[useLogin] Login mutation failed:', error);
    },
  });
};

// Mutation hook for registration
export const useRegister = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: RegisterData): Promise<User> => {
      try {
        return await AuthService.register(userData);
      } catch (error) {
        throw createApiError(error, 'User registration');
      }
    },
    onSuccess: () => {
      // Don't auto-login after registration, just invalidate queries
      invalidateQueries.auth();
    },
    onError: (error) => {
      if (import.meta.env.DEV) console.error('Registration mutation failed:', error);
    },
  });
};

// Mutation hook for logout
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      try {
        // AuthService.logout() agora chama o backend para invalidar sessões
        await AuthService.logout();
      } catch (error) {
        // Log error but still proceed with logout
        if (import.meta.env.DEV) console.error('[useLogout] Erro ao fazer logout:', error);
        createApiError(error, 'Logout');
      }
    },
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear();
      // Remove user from cache
      queryClient.removeQueries({ queryKey: queryKeys.auth.me });
    },
    onError: (error) => {
      if (import.meta.env.DEV) console.error('[useLogout] Logout mutation failed:', error);
      // Still clear cache even if logout fails
      queryClient.clear();
    },
  });
};

// Hook to check if user is authenticated (token + user data synced)
export const useIsAuthenticated = (): boolean => {
  const { data: user } = useMe();
  const hasToken = !!AuthService.getToken();
  const hasUser = !!user;

  // Verifica sincronização: deve ter ambos ou nenhum
  if (!hasToken && hasUser) {
    // Estado inconsistente, limpar token
    AuthService.clearToken();
  }

  return hasToken && hasUser;
};

// Hook to get auth state with loading and synchronization check
export const useAuthState = () => {
  const { data: user, isLoading, error } = useMe();
  const hasToken = !!AuthService.getToken();
  const hasUser = !!user;

  // Verifica sincronização entre token e dados do usuário
  const isAuthenticated = hasToken && hasUser;


  return {
    user,
    isLoading,
    error: error ? createApiError(error, 'Auth state') : null,
    isAuthenticated,
    hasToken,
    isSynced: hasToken === hasUser, // Indica se token e user estão sincronizados
  };
};